import type { BookingView, CurrentSession } from '@elite-residence/api-client';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useApiClient } from '../api/ApiClientProvider';
import { businessDate } from '../dates';
import { loadPendingFinance } from '../finance/loadPendingFinance';
import { type Dictionary, useLocale, useText } from '../i18n/LocaleProvider';
import {
  Chip,
  PageHead,
  StatusBar,
  shortDate,
  UNIT_STATE_LABEL,
  UnitGlyph,
  UnitSquare,
  UnitStatusChip,
} from '../ui/design';
import { Icon, type IconName } from '../ui/icons';
import { Loading, readResponse } from '../ui/ui';
import { NewDraft, STATUS_LABELS } from './BookingPages';
import { UNIT_STATES, type UnitState, unitState } from './board';
import { loadBookings } from './loadBookings';

// Unit availability board (approved prototype "Booking Operations"): every
// Unit of the permitted Branches as a tile grouped Branch -> Floor, coloured
// and shaped by its state for today or for chosen dates. All facts come from
// the server: availability, physical occupancy and blockers
// (getUnitAvailability), Bookings, Customers and pending finance. Nothing is
// decided here; actions open the existing server-checked flows.

type Booking = BookingView['booking'];

const text: Dictionary<
  | 'title'
  | 'units'
  | 'branches'
  | 'liveBookings'
  | 'board'
  | 'list'
  | 'newBooking'
  | 'today'
  | 'otherDates'
  | 'from'
  | 'to'
  | 'search'
  | 'allTypes'
  | 'compact'
  | 'detailed'
  | 'highlight'
  | 'arrivals'
  | 'departures'
  | 'overstay'
  | 'pending'
  | 'ofUnits'
  | 'onDates'
  | 'occupancyToday'
  | 'bookedForDates'
  | 'occupiedOf'
  | 'eligible'
  | 'free'
  | 'floor'
  | 'collapse'
  | 'allBranches'
  | 'out'
  | 'in'
  | 'until'
  | 'openEnded'
  | 'unit'
  | 'close'
  | 'openBooking'
  | 'maintenance'
  | 'openMaintenance'
  | 'unitSetup'
  | 'newForUnit'
  | 'startDate'
  | 'endDate'
  | 'continue'
  | 'freeNow'
  | 'heldBy'
  | 'noBlockers'
  | 'failed'
  | 'datesHint'
  | 'arrivesToday'
  | 'departsToday'
  | 'legend'
  | 'noUnits'
  | 'physical'
> = {
  en: {
    title: 'Booking Operations',
    units: 'units',
    branches: 'branches',
    liveBookings: 'live bookings',
    board: 'Unit board',
    list: 'Bookings',
    newBooking: 'New booking',
    today: 'Today',
    otherDates: 'Other dates',
    from: 'From',
    to: 'To',
    search: 'Search unit or customer',
    allTypes: 'All unit types',
    compact: 'Compact',
    detailed: 'Detailed',
    highlight: 'Highlight',
    arrivals: 'Arrivals today',
    departures: 'Departures today',
    overstay: 'Overstay',
    pending: 'Pending verification',
    ofUnits: 'of units',
    onDates: 'on these dates',
    occupancyToday: 'Occupancy today',
    bookedForDates: 'Booked for these dates',
    occupiedOf: 'occupied of',
    eligible: 'eligible units',
    free: 'free',
    floor: 'Floor',
    collapse: 'Collapse branch',
    allBranches: 'All branches',
    out: 'Out',
    in: 'In',
    until: 'until',
    openEnded: 'Open-ended',
    unit: 'Unit',
    close: 'Close',
    openBooking: 'Open booking',
    maintenance: 'Maintenance',
    openMaintenance: 'Open Maintenance',
    unitSetup: 'Open in Property setup',
    newForUnit: 'New booking for this unit',
    startDate: 'Start date',
    endDate: 'End date (departure)',
    continue: 'Continue',
    freeNow: 'Free for the selected dates.',
    heldBy: 'Held for the selected dates',
    noBlockers: 'No booking or maintenance overlaps the selected dates.',
    failed: 'The board could not be loaded.',
    datesHint: 'Statuses show the selected dates. A later reservation does not block an earlier stay.',
    arrivesToday: 'Arrives today',
    departsToday: 'Departs today',
    legend: 'Unit states',
    noUnits: 'No units in your scope.',
    physical: 'Physically occupied now',
  },
  ar: {
    title: 'عمليات الحجز',
    units: 'وحدة',
    branches: 'فروع',
    liveBookings: 'حجوزات جارية أو قادمة',
    board: 'لوحة الوحدات',
    list: 'الحجوزات',
    newBooking: 'حجز جديد',
    today: 'اليوم',
    otherDates: 'تواريخ أخرى',
    from: 'من',
    to: 'إلى',
    search: 'ابحث برقم الوحدة أو اسم العميل',
    allTypes: 'كل أنواع الوحدات',
    compact: 'مضغوط',
    detailed: 'تفصيلي',
    highlight: 'إبراز',
    arrivals: 'وصول اليوم',
    departures: 'مغادرة اليوم',
    overstay: 'تجاوز المدة',
    pending: 'بانتظار التحقق',
    ofUnits: 'من الوحدات',
    onDates: 'في هذه التواريخ',
    occupancyToday: 'الإشغال اليوم',
    bookedForDates: 'المحجوز في هذه التواريخ',
    occupiedOf: 'مشغولة من',
    eligible: 'وحدة مؤهلة',
    free: 'شاغرة',
    floor: 'الدور',
    collapse: 'طيّ الفرع',
    allBranches: 'كل الفروع',
    out: 'خروج',
    in: 'دخول',
    until: 'حتى',
    openEnded: 'مفتوحة',
    unit: 'الوحدة',
    close: 'إغلاق',
    openBooking: 'فتح الحجز',
    maintenance: 'الصيانة',
    openMaintenance: 'فتح الصيانة',
    unitSetup: 'فتح تهيئة العقار',
    newForUnit: 'حجز جديد لهذه الوحدة',
    startDate: 'تاريخ البداية',
    endDate: 'تاريخ النهاية (المغادرة)',
    continue: 'متابعة',
    freeNow: 'شاغرة في التواريخ المختارة.',
    heldBy: 'محجوزة في التواريخ المختارة',
    noBlockers: 'لا يوجد حجز أو صيانة يتقاطع مع التواريخ المختارة.',
    failed: 'تعذر تحميل لوحة الوحدات.',
    datesHint: 'الحالات تعرض التواريخ المختارة. الحجز اللاحق لا يمنع إقامة تسبقه.',
    arrivesToday: 'يصل اليوم',
    departsToday: 'يغادر اليوم',
    legend: 'حالات الوحدة',
    noUnits: 'لا توجد وحدات ضمن نطاقك.',
    physical: 'مشغولة فعلياً الآن',
  },
};

const has = (session: CurrentSession, key: string) => session.permissions.includes(key);
const FINANCE_QUEUE = ['finance.pending_manage', 'finance.noncash_verify', 'finance.booking_view'];

type Highlight = 'arrivals' | 'departures' | 'overstay' | 'pending';
const HIGHLIGHT_ICON: Record<Highlight, IconName> = {
  arrivals: 'login',
  departures: 'logout',
  overstay: 'alert',
  pending: 'clock',
};

function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

export function BoardPage({ session }: { readonly session: CurrentSession }) {
  const client = useApiClient();
  const t = useText(text);
  const { locale } = useLocale();
  const today = businessDate(new Date());
  const [mode, setMode] = useState<'today' | 'range'>('today');
  const [range, setRange] = useState({ from: addDays(today, 7), to: addDays(today, 37) });
  const [branchFilter, setBranchFilter] = useState('all');
  const [states, setStates] = useState<ReadonlySet<UnitState>>(new Set());
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [density, setDensity] = useState<'compact' | 'detailed'>('compact');
  const [highlight, setHighlight] = useState<Highlight | null>(null);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [selected, setSelected] = useState<string | null>(null);

  const period =
    mode === 'today'
      ? { start: today, end: addDays(today, 1) }
      : { start: range.from, end: range.to > range.from ? range.to : addDays(range.from, 1) };

  const branches = useQuery({
    queryKey: ['board', 'branches'],
    queryFn: async ({ signal }) =>
      readResponse(await client.listBranches({}, { signal })).branches.filter((b) => !b.archived),
  });
  const shownBranches = (branches.data ?? []).filter((b) => branchFilter === 'all' || b.id === branchFilter);
  const availability = useQueries({
    queries: shownBranches.map((branch) => ({
      queryKey: ['board', 'availability', branch.id, period.start, period.end],
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        readResponse(
          await client.getUnitAvailability(
            { query: { branchId: branch.id, start: period.start, end: period.end } },
            { signal },
          ),
        ).units,
    })),
  });
  const floors = useQueries({
    queries: shownBranches.map((branch) => ({
      queryKey: ['board', 'floors', branch.id],
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        readResponse(await client.listFloors({ path: { branchId: branch.id }, query: {} }, { signal })).floors,
    })),
  });
  const occupancy = useQueries({
    queries: shownBranches.map((branch) => ({
      queryKey: ['board', 'occupancy', branch.id, today],
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        readResponse(await client.currentOccupancy({ query: { branchId: branch.id, asOf: today } }, { signal })),
    })),
  });
  const unitTypes = useQuery({
    queryKey: ['reference', 'unit_type'],
    queryFn: async ({ signal }) =>
      readResponse(await client.listReferenceValues({ path: { kind: 'unit_type' } }, { signal })).values,
  });
  const bookings = useQuery({
    queryKey: ['board', 'bookings'],
    queryFn: async ({ signal }) => loadBookings(client, {}, signal),
  });
  const pending = useQuery({
    queryKey: ['board', 'pending-finance'],
    enabled: FINANCE_QUEUE.some((key) => has(session, key)),
    queryFn: async ({ signal }) => loadPendingFinance(client, signal),
  });

  const bookingById = useMemo(() => new Map((bookings.data ?? []).map((b) => [b.id, b])), [bookings.data]);
  // Names only for the Customers of Bookings that hold a unit on the board:
  // one basic-profile read each (the server refuses an unfiltered listing).
  const heldCustomerIds = useMemo(() => {
    const ids = new Set<string>();
    for (const result of availability) {
      for (const unit of result.data ?? []) {
        for (const conflict of unit.conflicts) {
          const booking = conflict.bookingId ? bookingById.get(conflict.bookingId) : undefined;
          if (booking) ids.add(booking.customerId);
        }
      }
    }
    return [...ids].sort();
  }, [availability, bookingById]);
  const customerReads = useQueries({
    queries: has(session, 'customer.basic_view')
      ? heldCustomerIds.map((customerId) => ({
          queryKey: ['board', 'customer', customerId],
          staleTime: 60_000,
          queryFn: async ({ signal }: { signal: AbortSignal }) =>
            readResponse(await client.getCustomer({ path: { customerId } }, { signal })).customer,
        }))
      : [],
  });
  const customerName = new Map(
    customerReads.flatMap((read) => (read.data ? [[read.data.id, read.data.name] as const] : [])),
  );
  const typeLabel = useMemo(
    () => new Map((unitTypes.data ?? []).map((v) => [v.id, v.labels[locale]])),
    [unitTypes.data, locale],
  );
  const pendingBookings = useMemo(() => new Set((pending.data ?? []).map((p) => p.bookingId)), [pending.data]);
  const inHouse = (bookingId: string) => {
    const booking = bookingById.get(bookingId);
    return Boolean(booking?.stay && !booking.stay.checkedOutAt);
  };

  const loading = branches.isPending || availability.some((q) => q.isPending) || floors.some((q) => q.isPending);
  const failed = branches.isError || availability.some((q) => q.isError) || floors.some((q) => q.isError);

  type Tile = {
    readonly unitId: string;
    readonly unitNumber: string;
    readonly branchId: string;
    readonly floorId: string;
    readonly unitTypeId: string;
    readonly state: UnitState;
    readonly overstay: boolean;
    readonly physicalOccupied: boolean;
    readonly booking: Booking | undefined;
    readonly conflicts: readonly {
      kind: 'booking' | 'maintenance';
      start: string;
      end: string | null;
      bookingId: string | null;
    }[];
  };
  const tiles: Tile[] = shownBranches.flatMap((branch, index) =>
    (availability[index]?.data ?? []).map((unit) => {
      const held = unit.conflicts.find((c) => c.kind === 'booking' && c.bookingId);
      return {
        unitId: unit.unitId,
        unitNumber: unit.unitNumber,
        branchId: branch.id,
        floorId: unit.floorId,
        unitTypeId: unit.unitTypeId,
        state: unitState(unit, { today: mode === 'today', inHouse }),
        overstay: unit.overstay,
        physicalOccupied: unit.physicalOccupied,
        booking: held?.bookingId ? bookingById.get(held.bookingId) : undefined,
        conflicts: unit.conflicts,
      };
    }),
  );

  const isArrival = (b: Booking | undefined) =>
    Boolean(b && b.start === today && !b.stay && (b.status === 'RESERVED' || b.status === 'ACTIVE'));
  const highlightTest: Record<Highlight, (tile: Tile) => boolean> = {
    arrivals: (tile) => isArrival(tile.booking),
    departures: (tile) => Boolean(tile.booking?.indicators.departureToday),
    overstay: (tile) => tile.overstay || Boolean(tile.booking?.indicators.overstay),
    pending: (tile) => Boolean(tile.booking && pendingBookings.has(tile.booking.id)),
  };
  const highlights: Highlight[] = [
    'arrivals',
    'departures',
    'overstay',
    ...(pending.data ? (['pending'] as const) : []),
  ];

  const q = query.trim().toLowerCase();
  const matches = (tile: Tile) => {
    if (states.size > 0 && !states.has(tile.state)) return false;
    if (typeFilter !== 'all' && tile.unitTypeId !== typeFilter) return false;
    if (highlight && !highlightTest[highlight](tile)) return false;
    if (q) {
      const name = tile.booking ? (customerName.get(tile.booking.customerId) ?? '') : '';
      const hay = `${tile.unitNumber} ${name} ${tile.booking?.displayReference ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };
  const countStates = (list: readonly Tile[]) => {
    const counts: Record<UnitState, number> = { available: 0, reserved: 0, occupied: 0, maintenance: 0 };
    for (const tile of list) counts[tile.state]++;
    return counts;
  };
  const counts = countStates(tiles);
  const total = tiles.length || 1;
  const liveCount = (bookings.data ?? []).filter((b) => b.status === 'RESERVED' || b.status === 'ACTIVE').length;

  const occupied = occupancy.reduce((sum, o) => sum + (o.data?.occupiedUnits ?? 0), 0);
  const eligible = occupancy.reduce((sum, o) => sum + (o.data?.eligibleUnits ?? 0), 0);
  const singleOccupancy = occupancy.length === 1 ? occupancy[0]?.data : undefined;

  const stateLabel = (state: UnitState) =>
    mode === 'range' && state === 'available' ? (locale === 'ar' ? 'شاغرة' : 'Free') : UNIT_STATE_LABEL[state][locale];

  const tileMeta = (tile: Tile) => {
    if (tile.state === 'maintenance') {
      const m = tile.conflicts.find((c) => c.kind === 'maintenance');
      return m?.end ? `${t.until} ${shortDate(locale, m.end)}` : t.openEnded;
    }
    if (tile.state === 'occupied' && tile.booking) return `${t.out} ${shortDate(locale, tile.booking.end)}`;
    if (tile.state === 'reserved') {
      const c = tile.conflicts.find((x) => x.kind === 'booking');
      return c ? `${t.in} ${shortDate(locale, c.start)}` : stateLabel(tile.state);
    }
    return typeLabel.get(tile.unitTypeId) ?? '';
  };

  const selectedTile = tiles.find((tile) => tile.unitId === selected) ?? null;

  return (
    <main>
      <PageHead
        title={t.title}
        sub={`${tiles.length} ${t.units} · ${branchFilter === 'all' ? `${shownBranches.length} ${t.branches}` : (shownBranches[0]?.name ?? '')} · ${liveCount} ${t.liveBookings}`}
        actions={
          <>
            <nav className="er-seg" aria-label={t.board}>
              <span className="er-seg__opt is-active" aria-current="page">
                <Icon name="board" />
                {t.board}
              </span>
              <Link className="er-seg__opt" to="/bookings">
                <Icon name="list" />
                {t.list}
              </Link>
            </nav>
            {has(session, 'booking.create') ? (
              <Link className="er-btn er-btn--primary" to="/bookings">
                <Icon name="plus" />
                {t.newBooking}
              </Link>
            ) : null}
          </>
        }
      />

      <div className="er-stats">
        {UNIT_STATES.map((state) => (
          <button
            key={state}
            type="button"
            className={`er-statfilter${states.has(state) ? ' is-active' : ''}`}
            aria-pressed={states.has(state)}
            onClick={() => {
              const next = new Set(states);
              if (next.has(state)) next.delete(state);
              else next.add(state);
              setStates(next);
            }}
          >
            <UnitSquare state={state} large />
            <span className="er-statfilter__n">{counts[state]}</span>
            <span>
              <span className="er-statfilter__l">{stateLabel(state)}</span>
              <span className="er-statfilter__p">
                {Math.round((counts[state] / total) * 100)}% {mode === 'range' ? t.onDates : t.ofUnits}
              </span>
            </span>
          </button>
        ))}
        <div className="er-panel er-occ">
          <div className="er-occ__row">
            <span className="er-overline">{mode === 'today' ? t.occupancyToday : t.bookedForDates}</span>
            <span className="er-occ__n">
              {mode === 'today'
                ? singleOccupancy?.occupancyPercent != null
                  ? `${singleOccupancy.occupancyPercent}%`
                  : `${occupied} / ${eligible}`
                : `${counts.occupied + counts.reserved} / ${tiles.length}`}
            </span>
          </div>
          <StatusBar counts={counts} />
          <div className="er-cell-sub">
            {mode === 'today'
              ? `${occupied} ${t.occupiedOf} ${eligible} ${t.eligible} · ${counts.available} ${t.free}`
              : `${shortDate(locale, period.start)} – ${shortDate(locale, period.end)} · ${counts.available} ${t.free}`}
          </div>
        </div>
      </div>

      <div className="er-tools">
        <fieldset className="er-seg">
          <legend className="er-visually-hidden">{t.today}</legend>
          <button
            type="button"
            className={`er-seg__opt${mode === 'today' ? ' is-active' : ''}`}
            aria-pressed={mode === 'today'}
            onClick={() => setMode('today')}
          >
            {t.today}
          </button>
          <button
            type="button"
            className={`er-seg__opt${mode === 'range' ? ' is-active' : ''}`}
            aria-pressed={mode === 'range'}
            onClick={() => setMode('range')}
          >
            {t.otherDates}
          </button>
        </fieldset>
        {mode === 'range' ? (
          <>
            <input
              type="date"
              aria-label={t.from}
              value={range.from}
              min={today}
              style={{ width: 150 }}
              onChange={(event) => setRange({ ...range, from: event.target.value })}
            />
            <span className="er-cell-sub">{t.to}</span>
            <input
              type="date"
              aria-label={t.to}
              value={range.to}
              min={range.from}
              style={{ width: 150 }}
              onChange={(event) => setRange({ ...range, to: event.target.value })}
            />
          </>
        ) : null}
        {(branches.data?.length ?? 0) > 1 ? (
          <fieldset className="er-seg">
            <legend className="er-visually-hidden">{t.allBranches}</legend>
            {[{ id: 'all', name: t.allBranches }, ...(branches.data ?? [])].map((branch) => (
              <button
                key={branch.id}
                type="button"
                className={`er-seg__opt${branchFilter === branch.id ? ' is-active' : ''}`}
                aria-pressed={branchFilter === branch.id}
                onClick={() => setBranchFilter(branch.id)}
              >
                {branch.name}
              </button>
            ))}
          </fieldset>
        ) : null}
        <span className="er-spacer" />
        <label className="er-inputgroup" style={{ width: 240 }}>
          <Icon name="search" />
          <input
            aria-label={t.search}
            placeholder={t.search}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <select
          aria-label={t.allTypes}
          value={typeFilter}
          style={{ width: 180 }}
          onChange={(event) => setTypeFilter(event.target.value)}
        >
          <option value="all">{t.allTypes}</option>
          {(unitTypes.data ?? []).map((type) => (
            <option key={type.id} value={type.id}>
              {type.labels[locale]}
            </option>
          ))}
        </select>
        <fieldset className="er-seg">
          <legend className="er-visually-hidden">{t.detailed}</legend>
          {(['compact', 'detailed'] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={`er-seg__opt${density === value ? ' is-active' : ''}`}
              aria-pressed={density === value}
              onClick={() => setDensity(value)}
            >
              {t[value]}
            </button>
          ))}
        </fieldset>
      </div>

      <section className="er-panel" aria-label={t.board}>
        <div className="er-board__hl">
          <span className="er-overline">{t.highlight}</span>
          {highlights.map((key) => (
            <button
              key={key}
              type="button"
              className={`er-hl${highlight === key ? ' is-active' : ''}`}
              aria-pressed={highlight === key}
              onClick={() => setHighlight(highlight === key ? null : key)}
            >
              <Icon name={HIGHLIGHT_ICON[key]} />
              {t[key]} <b>{tiles.filter(highlightTest[key]).length}</b>
            </button>
          ))}
          <span className="er-spacer" />
          {mode === 'range' ? <span className="er-cell-sub">{t.datesHint}</span> : null}
        </div>
        {loading ? (
          <div className="er-panel__body">
            <Loading />
          </div>
        ) : failed ? (
          <div className="er-panel__body">
            <p className="er-alert" role="alert">
              {t.failed}
            </p>
          </div>
        ) : tiles.length === 0 ? (
          <div className="er-empty">{t.noUnits}</div>
        ) : (
          <div className={`er-board${density === 'detailed' ? ' is-detailed' : ''}`}>
            {shownBranches.map((branch, index) => {
              const branchTiles = tiles.filter((tile) => tile.branchId === branch.id);
              const branchCounts = countStates(branchTiles);
              const branchFloors = floors[index]?.data ?? [];
              const isCollapsed = collapsed.has(branch.id);
              const floorIds = [
                ...branchFloors.map((f) => f.id).filter((id) => branchTiles.some((tile) => tile.floorId === id)),
                ...[...new Set(branchTiles.map((tile) => tile.floorId))].filter(
                  (id) => !branchFloors.some((f) => f.id === id),
                ),
              ];
              const branchOcc = occupancy[index]?.data;
              return (
                <section key={branch.id} className="er-branch">
                  <div className="er-branch__head">
                    <button
                      type="button"
                      className="er-btn er-btn--ghost er-btn--icon er-btn--sm"
                      aria-label={`${t.collapse}: ${branch.name}`}
                      aria-expanded={!isCollapsed}
                      onClick={() => {
                        const next = new Set(collapsed);
                        if (next.has(branch.id)) next.delete(branch.id);
                        else next.add(branch.id);
                        setCollapsed(next);
                      }}
                    >
                      <Icon
                        name={isCollapsed ? 'chev-right' : 'chev-down'}
                        className={isCollapsed ? 'er-flip' : undefined}
                      />
                    </button>
                    <Icon name="building" className="er-branch__ico" />
                    <h2 className="er-panel__title">{branch.name}</h2>
                    <span className="er-cell-sub">
                      {branchTiles.length} {t.units}
                      {branchOcc?.occupancyPercent != null && mode === 'today'
                        ? ` · ${t.occupancyToday} ${branchOcc.occupancyPercent}%`
                        : ''}
                    </span>
                    <span className="er-spacer" />
                    <span className="er-branch__counts">
                      {UNIT_STATES.map((state) => (
                        <span key={state} className={`k-${state}`}>
                          <UnitGlyph state={state} />
                          <span style={{ color: 'var(--ink-secondary)' }}>
                            {branchCounts[state]} {stateLabel(state)}
                          </span>
                        </span>
                      ))}
                    </span>
                    <span className="er-branch__bar">
                      <StatusBar counts={branchCounts} />
                    </span>
                  </div>
                  {isCollapsed ? null : (
                    <div className="er-floors">
                      {floorIds.map((floorId) => {
                        const floorTiles = branchTiles
                          .filter((tile) => tile.floorId === floorId)
                          .sort((a, b) => a.unitNumber.localeCompare(b.unitNumber, undefined, { numeric: true }));
                        const floor = branchFloors.find((f) => f.id === floorId);
                        return (
                          <div key={floorId} className="er-floor">
                            <div className="er-floor__label">
                              {floor ? floor.labels[locale] : t.floor}
                              <span>
                                {floorTiles.filter((tile) => tile.state === 'available').length} {t.free}
                              </span>
                            </div>
                            <div className="er-tiles">
                              {floorTiles.map((tile) => {
                                const mark =
                                  mode === 'today' && isArrival(tile.booking)
                                    ? 'login'
                                    : mode === 'today' && tile.booking?.indicators.departureToday
                                      ? 'logout'
                                      : tile.overstay
                                        ? 'alert'
                                        : null;
                                const guest =
                                  tile.state === 'maintenance'
                                    ? t.maintenance
                                    : tile.booking
                                      ? (customerName.get(tile.booking.customerId) ?? tile.booking.displayReference)
                                      : (typeLabel.get(tile.unitTypeId) ?? '');
                                const meta = tileMeta(tile);
                                return (
                                  <button
                                    key={tile.unitId}
                                    type="button"
                                    className={`er-unit er-unit--${tile.state}${density === 'detailed' ? ' er-unit--detailed' : ''}${matches(tile) ? '' : ' is-dim'}${selected === tile.unitId ? ' is-selected' : ''}`}
                                    aria-label={`${t.unit} ${tile.unitNumber}, ${stateLabel(tile.state)}${meta ? `, ${meta}` : ''}`}
                                    onClick={() => setSelected(tile.unitId)}
                                  >
                                    <span className="er-unit__no">
                                      <UnitGlyph state={tile.state} />
                                      <span dir="ltr">{tile.unitNumber}</span>
                                      {mark ? (
                                        <span
                                          style={{ marginInlineStart: 'auto', display: 'inline-flex' }}
                                          title={
                                            mark === 'login'
                                              ? t.arrivesToday
                                              : mark === 'logout'
                                                ? t.departsToday
                                                : t.overstay
                                          }
                                        >
                                          <Icon name={mark} className="er-flip" />
                                        </span>
                                      ) : null}
                                    </span>
                                    <span className="er-unit__guest">{guest}</span>
                                    <span className="er-unit__meta">{meta}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </section>

      {selectedTile ? (
        <UnitDrawer
          key={`${selectedTile.unitId}:${period.start}:${period.end}`}
          session={session}
          tile={selectedTile}
          branchName={shownBranches.find((b) => b.id === selectedTile.branchId)?.name ?? ''}
          floorLabel={
            floors[shownBranches.findIndex((b) => b.id === selectedTile.branchId)]?.data?.find(
              (f) => f.id === selectedTile.floorId,
            )?.labels[locale] ?? ''
          }
          typeLabel={typeLabel.get(selectedTile.unitTypeId) ?? ''}
          bookingById={bookingById}
          customerName={customerName}
          period={period}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </main>
  );
}

function UnitDrawer({
  session,
  tile,
  branchName,
  floorLabel,
  typeLabel,
  bookingById,
  customerName,
  period,
  onClose,
}: {
  readonly session: CurrentSession;
  readonly tile: {
    readonly unitId: string;
    readonly unitNumber: string;
    readonly state: UnitState;
    readonly physicalOccupied: boolean;
    readonly overstay: boolean;
    readonly conflicts: readonly {
      kind: 'booking' | 'maintenance';
      start: string;
      end: string | null;
      bookingId: string | null;
    }[];
  };
  readonly branchName: string;
  readonly floorLabel: string;
  readonly typeLabel: string;
  readonly bookingById: ReadonlyMap<string, Booking>;
  readonly customerName: ReadonlyMap<string, string>;
  readonly period: { readonly start: string; readonly end: string };
  readonly onClose: () => void;
}) {
  const t = useText(text);
  const { locale } = useLocale();
  const heading = useRef<HTMLHeadingElement>(null);
  const [draft, setDraft] = useState<{ start: string; end: string; open: boolean }>({
    start: period.start,
    end: period.end,
    open: false,
  });
  // Remounted per unit and dates (key), so the heading takes focus on open.
  useEffect(() => {
    heading.current?.focus();
  }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const canSetup = ['property.setup', 'property.reference_price', 'unit.archive'].some((key) =>
    session.permissions.includes(key),
  );
  const conflicts = [...tile.conflicts].sort((a, b) => a.start.localeCompare(b.start));
  return (
    <>
      <div className="er-scrim" aria-hidden="true" onClick={onClose} />
      <aside className="er-drawer" role="dialog" aria-modal="true" aria-labelledby="unit-drawer-title">
        <div className="er-drawer__head">
          <UnitSquare state={tile.state} large />
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 className="er-drawer__title" id="unit-drawer-title" ref={heading} tabIndex={-1}>
              {t.unit} <span dir="ltr">{tile.unitNumber}</span>
            </h2>
            <div className="er-drawer__sub">{[typeLabel, floorLabel, branchName].filter(Boolean).join(' · ')}</div>
          </div>
          <button
            className="er-btn er-btn--ghost er-btn--icon er-btn--sm"
            type="button"
            aria-label={t.close}
            onClick={onClose}
          >
            <Icon name="x" />
          </button>
        </div>
        <div className="er-drawer__body">
          <div className="er-dsection">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <UnitStatusChip state={tile.state} />
              <span className="er-cell-sub">
                {shortDate(locale, period.start)} – {shortDate(locale, period.end)}
              </span>
              {tile.physicalOccupied ? <Chip tone="neutral">{t.physical}</Chip> : null}
              {tile.overstay ? <Chip tone="danger">{t.overstay}</Chip> : null}
            </div>
            {conflicts.length === 0 ? <div className="er-cell-sub">{t.noBlockers}</div> : null}
          </div>
          {conflicts.map((conflict) => {
            if (conflict.kind === 'maintenance') {
              return (
                <div className="er-dsection" key={`m-${conflict.start}`}>
                  <span className="er-overline">{t.maintenance}</span>
                  <dl className="er-kv">
                    <dt>{t.startDate}</dt>
                    <dd>{shortDate(locale, conflict.start)}</dd>
                    <dt>{t.until}</dt>
                    <dd>{conflict.end ? shortDate(locale, conflict.end) : t.openEnded}</dd>
                  </dl>
                  <Link className="er-btn er-btn--secondary er-btn--sm" to="/maintenance">
                    <Icon name="wrench" />
                    {t.openMaintenance}
                  </Link>
                </div>
              );
            }
            const booking = conflict.bookingId ? bookingById.get(conflict.bookingId) : undefined;
            return (
              <div className="er-dsection" key={`b-${conflict.bookingId ?? conflict.start}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="er-overline">{t.heldBy}</span>
                  <span className="er-spacer" />
                  {booking ? (
                    <Chip tone={booking.status === 'ACTIVE' ? 'brand' : 'info'}>
                      {STATUS_LABELS[booking.status]?.[locale] ?? booking.status}
                    </Chip>
                  ) : null}
                </div>
                {booking ? (
                  <>
                    <div className="er-cell-main" style={{ fontSize: 14 }}>
                      {customerName.get(booking.customerId) ?? booking.displayReference}
                    </div>
                    <dl className="er-kv">
                      <dt>{locale === 'ar' ? 'المرجع' : 'Reference'}</dt>
                      <dd className="er-mono">{booking.displayReference}</dd>
                      <dt>{locale === 'ar' ? 'الإقامة' : 'Stay'}</dt>
                      <dd>
                        {shortDate(locale, booking.start)} – {shortDate(locale, booking.end)}
                      </dd>
                      <dt>{locale === 'ar' ? 'الإجمالي المتفق عليه' : 'Agreed total'}</dt>
                      <dd dir="ltr">SAR {booking.effectiveTotal}</dd>
                    </dl>
                    <Link className="er-btn er-btn--primary" to={`/bookings/${booking.id}`}>
                      {t.openBooking}
                      <Icon name="arrow" className="er-flip" />
                    </Link>
                  </>
                ) : (
                  <div className="er-cell-sub">
                    {shortDate(locale, conflict.start)} – {shortDate(locale, conflict.end)}
                  </div>
                )}
              </div>
            );
          })}
          {tile.state === 'available' && session.permissions.includes('booking.create') ? (
            <div className="er-dsection">
              <span className="er-overline">{t.newForUnit}</span>
              {draft.open ? (
                <NewDraft unitId={tile.unitId} unitNumber={tile.unitNumber} start={draft.start} end={draft.end} />
              ) : (
                <>
                  <div className="er-formgrid">
                    <div className="er-field">
                      <label htmlFor="board-draft-start">{t.startDate}</label>
                      <input
                        id="board-draft-start"
                        type="date"
                        value={draft.start}
                        onChange={(event) => setDraft({ ...draft, start: event.target.value })}
                      />
                    </div>
                    <div className="er-field">
                      <label htmlFor="board-draft-end">{t.endDate}</label>
                      <input
                        id="board-draft-end"
                        type="date"
                        value={draft.end}
                        min={draft.start}
                        onChange={(event) => setDraft({ ...draft, end: event.target.value })}
                      />
                    </div>
                  </div>
                  <button
                    className="er-btn er-btn--primary"
                    type="button"
                    disabled={!draft.start || !draft.end || draft.end <= draft.start}
                    onClick={() => setDraft({ ...draft, open: true })}
                  >
                    <Icon name="plus" />
                    {t.continue}
                  </button>
                </>
              )}
            </div>
          ) : null}
        </div>
        <div className="er-drawer__foot">
          {canSetup ? (
            <Link className="er-btn er-btn--secondary" to={`/property/units/${tile.unitId}`}>
              <Icon name="settings" />
              {t.unitSetup}
            </Link>
          ) : null}
          <span className="er-spacer" />
          <button className="er-btn er-btn--secondary" type="button" onClick={onClose}>
            {t.close}
          </button>
        </div>
      </aside>
    </>
  );
}
